import React from 'react';
import { ArrowLeft, Check, X, Minus, Sparkles, Zap, Shield, Globe2, Cpu, GraduationCap, Video, BookOpen, MessageSquare, Quote, Star, CheckCircle2, HelpCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { SEO } from '@/components/SEO';

export function AboutPage({ onBack }: { onBack: () => void }) {
  const comparisonData = [
    { name: "Doubtnut", rt: "negative", lang: "positive", doubt: "neutral", pers: "negative", vid: "neutral", label: "⚠️ Fixed" },
    { name: "Photomath", rt: "neutral", lang: "negative", doubt: "negative", pers: "negative", vid: "negative", label: "❌" },
    { name: "ChatGPT", rt: "positive", lang: "positive", doubt: "positive", pers: "neutral", vid: "negative", label: "❌" },
    { name: "Khanmigo", rt: "positive", lang: "negative", doubt: "positive", pers: "neutral", vid: "negative", label: "❌" },
    { name: "Professor X", rt: "positive", lang: "positive", doubt: "positive", pers: "positive", vid: "positive", label: "✅ Full", active: true }
  ];

  const testimonials = [
    {
      role: "JEE Aspirant",
      text: "Professor X genuinely feels like having a personal IIT teacher available 24/7. The visual explanations and animated step-by-step solving made Coordinate Geometry and Calculus much easier to understand than normal YouTube lectures. The best part is it explains my exact question instantly.",
      gradient: "from-indigo-500/20 to-purple-500/20"
    },
    {
      role: "NEET Aspirant",
      text: "Biology diagrams and Physics numericals became much easier after using Professor X. The AI explanation videos are very clear and the notes generated after every solution save a lot of revision time. It’s honestly one of the most futuristic study tools I’ve used.",
      gradient: "from-emerald-500/20 to-teal-500/20"
    },
    {
      role: "CAT Aspirant",
      text: "As a CAT student, speed and conceptual clarity matter a lot. Professor X explains Quant problems in a very smart and visual way. Instead of memorizing shortcuts, I actually understood the logic behind the questions.",
      gradient: "from-amber-500/20 to-orange-500/20"
    },
    {
      role: "Engineering Student",
      text: "I used Professor X for Engineering Mathematics and DSA concepts, and I was shocked by how accurately it generated full explanation videos with graphs and notes. It feels more interactive than traditional online learning platforms.",
      gradient: "from-blue-500/20 to-cyan-500/20"
    },
    {
      role: "UPSC Aspirant",
      text: "The multilingual explanations and adaptive learning style are extremely useful for UPSC preparation. Complex Economy and Geography concepts become much easier when explained visually through diagrams and AI-generated teaching videos.",
      gradient: "from-rose-500/20 to-pink-500/20"
    },
    {
      role: "General Student",
      text: "Professor X is not just another doubt-solving app. It actually teaches concepts visually like a real tutor. The AI avatar explanations, notes, and instant solutions make studying much more engaging and less stressful.",
      gradient: "from-violet-500/20 to-fuchsia-500/20"
    }
  ];

  const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'positive') return <Check className="w-5 h-5 text-emerald-400" />;
    if (status === 'negative') return <X className="w-5 h-5 text-rose-400" />;
    return <Minus className="w-5 h-5 text-amber-400" />;
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-slate-200 relative">
      <SEO 
        title="About Professor X AI | Visual AI Tutor & Platform"
        description="Learn about Professor X AI - an advanced educational platform delivering step-by-step visual AI explanations, OneShot masterclasses, and voice narration in 21+ languages."
        canonical="https://professorxai.info/about"
      />
      {/* Background Orbital Decorations */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] -z-10 animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[700px] h-[700px] bg-purple-600/5 rounded-full blur-[150px] -z-10" />

      <div className="flex items-center justify-between mb-16">
        <motion.button 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack}
          className="flex items-center gap-3 text-indigo-400 hover:text-indigo-300 font-bold transition-all bg-white/5 hover:bg-white/10 px-8 py-4 rounded-2xl group border border-white/5 hover:border-white/10 shadow-2xl backdrop-blur-md"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Return to Experience
        </motion.button>

        {/* Brand Logo in Header */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-4 bg-white/5 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/10 shadow-2xl"
        >
          <div className="bg-indigo-600 p-1.5 rounded-xl shadow-[0_0_20px_rgba(79,70,229,0.4)]">
            <img src="/profxlogo.png" alt="Professor X Logo" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-2xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">PROFESSOR X</span>
        </motion.div>
      </div>

      <div className="space-y-48">
        {/* Cinematic Hero Section */}
        <section className="relative">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-center bg-slate-900/40 backdrop-blur-2xl p-12 sm:p-24 rounded-[4rem] border border-white/10 shadow-[0_64px_128px_-32px_rgba(0,0,0,0.7)] relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/5 pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none" />
            
            <div className="relative z-10 flex flex-col items-center">
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="mb-10 p-1 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-[2.5rem] shadow-[0_0_50px_rgba(99,102,241,0.3)]"
              >
                <div className="w-48 h-48 p-6 rounded-[2.2rem] bg-slate-950 flex items-center justify-center">
                   <img src="/profxlogo.png" alt="Professor X Logo" className="w-full h-full object-contain" />
                </div>
              </motion.div>
              
              <h1 className="text-7xl sm:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-slate-100 to-slate-500 tracking-tight pb-6 leading-[1.1]">
                Revolutionizing <br /> Learning with AI
              </h1>
              
              <p className="text-2xl sm:text-3xl text-indigo-400 font-bold max-w-4xl mx-auto leading-relaxed mt-4 drop-shadow-[0_0_15px_rgba(129,140,248,0.4)]">
                Not just an answer generator, but an AI teaching system designed to understand your learning requirement, structure the lesson, and guide you to mastery.
              </p>
              
              <div className="w-32 h-1.5 bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent my-12" />
              
              <p className="text-2xl text-white max-w-5xl mx-auto leading-relaxed font-medium">
                Professor X AI is an intelligent pedagogical engine that replicates the personalized attention of an elite master tutor. It delivers voice-narrated, visually augmented solutions, structured board presentations, and comprehensive OneShot masterclasses across 21+ global and Indian languages (featuring native Sarvam AI voices for Indian regional languages).
              </p>

              {/* Learning Workflow Diagram */}
              <div className="mt-14 w-full max-w-5xl mx-auto p-8 rounded-3xl bg-slate-950/80 border border-white/10 text-left">
                <div className="text-xs font-black uppercase tracking-widest text-indigo-400 mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Systematic Learning Workflow
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 text-sm sm:text-base font-semibold text-slate-200">
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Question / Topic / Syllabus</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">AI Understanding</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Lesson Planning</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Concept Explanation</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Structured Board Teaching</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Relevant Visuals</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Examples & Demos</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Practice & Questions</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-indigo-300">Exam Revision</span>
                  <span className="text-indigo-400 font-bold">→</span>
                  <span className="px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-emerald-400 border-emerald-500/30">Study Notes & Learning Video</span>
                </div>
              </div>

              {/* SEO Content: The Future of AI Education */}
              <div className="mt-16 text-left w-full max-w-4xl mx-auto border-t border-white/10 pt-10">
                <h2 className="text-3xl font-black text-white mb-4">The Future Of AI Education</h2>
                <p className="text-xl text-slate-300 leading-relaxed">
                  Professor X AI is redefining digital education through artificial intelligence, visual teaching, animated avatar presence, and personalized learning experiences. Our mission is to make high-quality conceptual education accessible, interactive, and engaging for every student through next-generation AI tutoring technology.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Feature Grid - Futuristic Style */}
        <section className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div {...fadeInUp} className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-black tracking-widest uppercase mb-2">
                <Cpu className="w-3 h-3" /> Architecture
              </div>
              <h2 className="text-5xl sm:text-6xl font-black text-white tracking-tight leading-tight">
                Designed for <br /> 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-indigo-600">Cognitive Clarity</span>
              </h2>
            </div>
            <p className="text-3xl text-white leading-relaxed font-light">
              We've engineered Professor X to optimize for how the human brain actually learns: through visual association, logical sequencing, and auditory reinforcement.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 pt-6">
              {[
                { title: "Atomic Steps", desc: "Complex problems pulverized into manageable logical units.", icon: Zap },
                { title: "Visual Logic", desc: "Dynamic diagrams that activate spatial reasoning.", icon: Video },
                { title: "Native Flow", desc: "Fluent narration in the world's most spoken languages.", icon: Globe2 },
                { title: "Infinite Patience", desc: "A tutor that never tires, available 24/7 for you.", icon: Shield }
              ].map((item, i) => (
                <div key={i} className="p-8 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 group">
                  <item.icon className="w-8 h-8 text-indigo-400 mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-lg text-white leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-8"
          >
            {[
              { label: 'Languages Supported', value: '21+' },
              { label: 'Academic Subjects', value: '100+' },
              { label: 'Learning Modules', value: '1 Million+' },
              { label: 'Real-time AI Engine', value: '2.0' }
            ].map((stat, i) => (
              <div key={i} className={`p-10 rounded-[3rem] bg-slate-900/60 border border-white/5 backdrop-blur-xl flex flex-col justify-center text-center shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-indigo-500/30 ${i === 0 ? 'bg-gradient-to-br from-indigo-600/20 to-transparent' : ''}`}>
                <div className="text-6xl font-black text-white mb-3 tracking-tighter">{stat.value}</div>
                <div className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </section>

        {/* The "Why" - Immersive Bento Grid */}
        <section className="space-y-16">
          <div className="text-center space-y-6">
            <h2 className="text-6xl font-black text-white tracking-tight">The User Experience</h2>
            <p className="text-2xl text-white max-w-3xl mx-auto">Seamlessly bridging the gap between curiosity and mastery.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div {...fadeInUp} className="md:col-span-2 p-10 rounded-[3rem] bg-gradient-to-br from-indigo-600/30 to-slate-900 border border-white/10 flex items-center gap-10 overflow-hidden relative group">
              <div className="space-y-6 relative z-10">
                <h3 className="text-3xl font-black text-white tracking-tight">OneShot Learning & Study From Your Materials</h3>
                <p className="text-xl text-white leading-relaxed max-w-xl">Upload textbook chapters, class notes, or syllabus PDFs with "Study From Your Materials" or enter any syllabus topic to generate 15-20+ minute modulewise masterclasses, complete with 7-module roadmaps, structured board teaching, and exam-oriented practice questions.</p>
                <div className="flex flex-wrap gap-3">
                  <div className="px-5 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-[10px] font-black uppercase tracking-widest text-white">OneShot Masterclass</div>
                  <div className="px-5 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-[10px] font-black uppercase tracking-widest text-white">Study From Your Materials</div>
                  <div className="px-5 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/10 text-[10px] font-black uppercase tracking-widest text-white">Exam Q&A</div>
                </div>
              </div>
              <div className="absolute right-[-60px] bottom-[-40px] opacity-10 group-hover:opacity-20 transition-opacity">
                <img src="/profxlogo.png" alt="" className="w-80 h-80 rotate-12 grayscale brightness-200" />
              </div>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.1 }} className="p-10 rounded-[3rem] bg-slate-900/50 border border-white/5 backdrop-blur-2xl flex flex-col justify-between group hover:border-indigo-500/30 transition-all">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-10 group-hover:bg-indigo-600/20 transition-colors">
                <MessageSquare className="w-8 h-8 text-indigo-400" />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white mb-4 tracking-tight">Deep Doubt Solving</h3>
                <p className="text-lg text-white leading-relaxed">Ask any conceptual doubt, solve complex numericals, or seek follow-up clarifications with synchronized audio narration and animated avatar playback.</p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="p-10 rounded-[3rem] bg-slate-900/50 border border-white/5 backdrop-blur-2xl flex flex-col justify-between group hover:border-amber-500/30 transition-all">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mb-10 group-hover:bg-amber-600/20 transition-colors">
                <BookOpen className="w-8 h-8 text-amber-400" />
              </div>
              <div>
                 <h3 className="text-2xl font-black text-white mb-4 tracking-tight">Dynamic Study Notes</h3>
                <p className="text-lg text-white leading-relaxed">Instantly export structured study notes, formulas, labeled educational diagrams, key insights, and downloadable MP4 learning videos.</p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.3 }} className="md:col-span-2 p-10 rounded-[3rem] bg-gradient-to-br from-slate-900 to-indigo-900/40 border border-white/10 flex items-center justify-between overflow-hidden relative group">
              <div className="space-y-6">
                <h3 className="text-3xl font-black text-white tracking-tight">Animated Professor X Presence & Narration</h3>
                <p className="text-xl text-white leading-relaxed max-w-lg">Experience continuous teaching with the animated Professor X avatar, synchronized word-by-word captions, and natural academic voice synthesis in 21+ languages (with Sarvam AI voice integration for Indian languages).</p>
              </div>
              <div className="hidden lg:flex items-center gap-1.5 pr-10">
                {[4, 10, 6, 12, 18, 12, 6, 8, 14, 10].map((h, i) => (
                  <motion.div 
                    key={i}
                    animate={{ height: [h*2, h * 3, h*2] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.1 }}
                    className="w-1.5 bg-indigo-500/60 rounded-full" 
                    style={{ height: h*2 }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Specialized AI Learning Engines Breakdown */}
        <section className="space-y-16">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(79,70,229,0.2)]">
              <Sparkles className="w-4 h-4 text-indigo-400" /> Deep Architectural Comparison
            </div>
            <h2 className="text-5xl sm:text-6xl font-black text-white tracking-tight">
              Dual Specialized AI Teaching Engines
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Professor X AI combines two distinct teaching engines designed specifically for distinct cognitive workflows: immediate doubt resolution vs. complete chapter mastery.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Engine 1: Doubt Solving Mode Detailed Box */}
            <motion.div 
              {...fadeInUp}
              className="p-10 rounded-[3.5rem] bg-slate-900/80 border border-indigo-500/30 backdrop-blur-3xl space-y-8 relative overflow-hidden group shadow-2xl flex flex-col justify-between hover:border-indigo-500/50 transition-all duration-500"
            >
              <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none group-hover:bg-indigo-500/20 transition-all duration-500" />
              
              <div className="space-y-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shadow-[0_0_20px_rgba(79,70,229,0.3)]">
                    <HelpCircle className="w-8 h-8" />
                  </div>
                  <span className="px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                    Engine 01 • Instant Doubt Resolution
                  </span>
                </div>

                <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  Doubt Solving Mode
                </h3>

                <p className="text-slate-300 text-lg leading-relaxed">
                  Doubt Solving Mode is an intelligent real-time problem-solving engine engineered to resolve specific academic questions, complex numerical calculations, handwritten equations, and conceptual queries on demand. Rather than returning flat text answers, it acts as an elite private tutor guiding you through the visual solution step-by-step.
                </p>

                <div className="space-y-4 pt-2">
                  <h4 className="text-sm font-black uppercase tracking-widest text-indigo-400 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Core Technical & Pedagogical Features
                  </h4>
                  <ul className="space-y-3.5 text-slate-300 text-base">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Multi-Modal Input Processing:</strong> Accepts doubts via natural text, voice dictation, handwritten problem photos, or textbook page scans.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Structured Chalkboard Teaching:</strong> Writes solutions onto a virtual chalkboard using UPPERCASE HEADINGS, bulleted logic, and cleanly rendered LaTeX formulas ($$...$$).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Synchronized Voice Narration:</strong> Natural voice teaching in 21+ global & Indian languages (powered by Sarvam AI for native regional Indian voices and ElevenLabs) with real-time word-by-word caption sync.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Context-Aware Educational Visuals:</strong> Automatically generates or displays topic-specific educational diagrams, right triangles, geometry graphs, and circuit schematics.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Interactive Follow-Up Doubt Chat:</strong> Continuously chat with Professor X after solution generation to clarify intermediate algebraic steps or conceptual edge cases.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 relative z-10 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-indigo-300">
                <span>Best for: Daily Homework, Test Questions & Numerical Doubts</span>
                <span>⚡ Real-Time Engine</span>
              </div>
            </motion.div>

            {/* Engine 2: OneShot Learning Mode Detailed Box */}
            <motion.div 
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="p-10 rounded-[3.5rem] bg-slate-900/80 border border-purple-500/30 backdrop-blur-3xl space-y-8 relative overflow-hidden group shadow-2xl flex flex-col justify-between hover:border-purple-500/50 transition-all duration-500"
            >
              <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none group-hover:bg-purple-500/20 transition-all duration-500" />
              
              <div className="space-y-6 relative z-10">
                <div className="flex items-center justify-between">
                  <div className="w-16 h-16 rounded-2xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-400 shadow-[0_0_20px_rgba(168,85,247,0.3)]">
                    <BookOpen className="w-8 h-8" />
                  </div>
                  <span className="px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest">
                    Engine 02 • Chapter Mastery
                  </span>
                </div>

                <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  OneShot Learning Mode
                </h3>

                <p className="text-slate-300 text-lg leading-relaxed">
                  OneShot Learning Mode is a comprehensive masterclass teaching engine built for complete chapter and topic mastery. Designed to replace hours of fragmented video lectures, it generates a full 15–20+ minute structured lesson complete with 7 core modules, deep derivations, and exam practice.
                </p>

                <div className="space-y-4 pt-2">
                  <h4 className="text-sm font-black uppercase tracking-widest text-purple-400 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" /> Core Technical & Pedagogical Features
                  </h4>
                  <ul className="space-y-3.5 text-slate-300 text-base">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Syllabus & Notes PDF Analysis:</strong> Upload textbook chapters, classwork notes, or syllabus PDFs to generate targeted exam-oriented lessons.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong>7-Module Master Roadmap:</strong> Progresses through Foundations, Core Principles, Mathematical Mechanisms, Worked Examples, Practical Demos, Exam Questions, and Summary.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong>15–20+ Minute Audio-Validated Depth:</strong> Multi-part generation pipeline producing 2,300–2,800+ spoken words across 15–25 comprehensive steps.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Exam-Oriented Practice & Revision:</strong> Includes competitive exam numericals, multiple-choice questions, key takeaways, and revision points.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span><strong>Complete Export & Video Generation:</strong> Export full structured study notes or generate a downloadable MP4 video for offline study.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 relative z-10 flex items-center justify-between text-xs font-bold uppercase tracking-widest text-purple-300">
                <span>Best for: Chapter Preparation, Exam Revision & Syllabus Mastery</span>
                <span>🎓 7-Module Masterclass</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Student Feedback Section - Premium Testimonials */}
        <section className="space-y-16">
          <div className="text-center space-y-6">
            <h2 className="text-6xl font-black text-white tracking-tight">Student Perspectives</h2>
            <p className="text-2xl text-white max-w-3xl mx-auto">Real success stories from the frontlines of digital learning.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div 
                key={i} 
                {...fadeInUp} 
                transition={{ delay: i * 0.1 }}
                className={`group relative p-10 rounded-[3rem] bg-slate-900/50 border border-white/5 backdrop-blur-3xl flex flex-col justify-between overflow-hidden transition-all duration-500 hover:border-indigo-500/30 hover:-translate-y-2 shadow-2xl`}
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${t.gradient} blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-1 mb-8">
                    {[1,2,3,4,5].map(star => <Star key={star} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
                  </div>
                  
                  <Quote className="w-12 h-12 text-indigo-500/20 absolute -top-4 -left-4 -z-10" />
                  <p className="text-xl text-white leading-relaxed font-medium mb-10 italic">
                    "{t.text}"
                  </p>
                </div>

                <div className="relative z-10 flex items-center justify-between pt-6 border-t border-white/5">
                  <div className="font-black text-xs uppercase tracking-[0.2em] text-indigo-400">
                    {t.role}
                  </div>
                  <Sparkles className="w-5 h-5 text-indigo-500/40" />
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* The Comparison - High Fidelity Table */}
        <section className="space-y-20">
          <div className="text-center space-y-6">
            <h2 className="text-5xl font-black text-white tracking-tight">Elite Benchmarking</h2>
            <p className="text-2xl text-white max-w-3xl mx-auto">See how Professor X compares to standard ed-tech solutions.</p>
          </div>

          <div className="relative group p-1 bg-gradient-to-br from-indigo-500/20 via-white/5 to-purple-500/20 rounded-[4rem]">
            <div className="overflow-x-auto rounded-[3.9rem]">
              <div className="min-w-[1000px] bg-[#020205] backdrop-blur-3xl overflow-hidden p-8">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-10 px-6 text-left font-black text-slate-600 text-[10px] uppercase tracking-[0.3em]">Success Metrics</th>
                      {comparisonData.map((p, i) => (
                        <th key={i} className="py-10 px-6 text-center">
                          <div className={`font-black text-xl tracking-tighter ${p.active ? 'text-indigo-400' : 'text-white/60'}`}>
                            {p.name}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { l: "AI Voice Narration", k: "rt" },
                      { l: "21+ Multi-Language Engine", k: "lang" },
                      { l: "Contextual Doubt Chat", k: "doubt" },
                      { l: "Hyper-Personalized Content", k: "pers" },
                      { l: "Cinematic Video Generation", k: "vid" }
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors group/row">
                        <td className="py-8 px-6 font-bold text-white group-hover/row:text-indigo-300 transition-colors">{row.l}</td>
                        {comparisonData.map((p, j) => (
                          <td key={j} className="py-8 px-6 text-center">
                            <div className="flex justify-center">
                              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${p.active ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-white/5'}`}>
                                <StatusIcon status={(p as any)[row.k]} />
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td className="py-12 px-6" />
                      {comparisonData.map((p, i) => (
                        <td key={i} className="py-12 px-6 text-center">
                          <span className={`text-[10px] font-black tracking-widest uppercase py-2 px-5 rounded-full border ${p.active ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10' : 'text-slate-600 border-white/5 bg-white/5'}`}>
                            {p.label}
                          </span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Global Impact CTA */}
        <section className="pb-32">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-indigo-950/40 backdrop-blur-3xl border border-indigo-500/30 p-16 sm:p-24 rounded-[4rem] text-center relative overflow-hidden group shadow-[0_0_100px_rgba(79,70,229,0.15)]"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-700/10 opacity-50" />
            <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" />
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]" />
            
            <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-5xl sm:text-6xl font-black text-white tracking-tight mb-8">
                Join the Educational <br /> Frontier
              </h2>
              <p className="text-2xl sm:text-3xl text-white max-w-4xl mx-auto leading-relaxed font-medium mb-12 opacity-90">
                Professor X is scaling rapidly to reach every learner on the planet. <br />
                Experience the next paradigm of cognitive enhancement today.
              </p>
              
              <div id="contact-section" className="flex flex-col items-center gap-10">
                <a 
                  href="mailto:professorxai108@gmail.com" 
                  className="bg-indigo-600 text-white px-16 py-8 rounded-[2.5rem] text-2xl font-black tracking-tight hover:bg-indigo-500 hover:scale-105 active:scale-95 transition-all shadow-2xl flex items-center gap-6"
                >
                  📩 professorxai108@gmail.com
                </a>
                
                <div className="flex items-center gap-12 text-indigo-300 font-black text-xs uppercase tracking-widest pt-10 border-t border-white/10 w-full justify-center">
                  <span>Available Globally</span>
                  <span>•</span>
                  <span>21+ Languages</span>
                  <span>•</span>
                  <span>24/7 AI Tutor</span>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
}
